import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TableWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import { applyContentItemOverrides } from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import {
	GetSocialListeningMyBrandAnalyticsTrendingHashtagsService,
	TrendingHashtag
} from '../../../../../../services/get-social-listening-my-brand-analytics-trending-hashtags.service';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

// Categorical bar palette — one color per hashtag row, in order, mirroring
// the LOLLY report mockup (Figma 13302:101655). Cycles if there are >10 rows.
const BAR_COLORS = [
	'#2872e1',
	'#e94f6a',
	'#2bb673',
	'#a98cf0',
	'#f5c451',
	'#6cd6c8',
	'#f0a3c6',
	'#5b8def',
	'#9aa7c7',
	'#cbb892'
];

interface HashtagBar {
	hashtag: string;
	count: number;
	color: string;
	widthPct: number;
}

@Component({
	standalone: true,
	selector: 'app-trending-hashtags-slide',
	templateUrl: './trending-hashtags-slide.component.html',
	styleUrls: ['./trending-hashtags-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		DecimalPipe,
		IconComponent,
		TypographyComponent,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class TrendingHashtagsSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();
	private destroy$ = new Subject<void>();
	private cachedHashtags: TrendingHashtag[] | null = null;
	private cacheKey: string | null = null;

	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';
	bars: HashtagBar[] = [];
	topHashtag = '';
	topHashtagLabel = '';
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'trending_hashtags',
		null,
		null
	);

	get isEmpty(): boolean {
		return !this.bars.length;
	}

	constructor(
		private analyticsService: GetSocialListeningMyBrandAnalyticsTrendingHashtagsService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedHashtags && this.cacheKey === currentCacheKey) {
			this.applyHashtags(this.cachedHashtags);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(hashtags => {
				this.cachedHashtags = hashtags;
				this.cacheKey = currentCacheKey;
				this.applyHashtags(hashtags);
			});
	}

	private applyHashtags(hashtags: TrendingHashtag[]): void {
		const rawSlide = this.buildSlide(hashtags);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(hashtags: TrendingHashtag[]): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		return {
			id: `${prefix}_slide`,
			slideType: 'trending_hashtags',
			title: this.item.title || 'Trending Hashtags',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Trending Hashtags',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				{
					id: `${prefix}_table`,
					kind: 'table',
					visible: true,
					content: {
						dataSourceKey: 'trending_hashtags_table',
						accentColor: '#2872e1',
						columns: [
							{ key: 'hashtag', label: 'Hashtag' },
							{ key: 'mentions', label: 'Mentions' }
						],
						rows: hashtags.map(h => ({
							hashtag: `#${h.hashtag.replace(/^#+/, '')}`,
							mentions: String(h.count)
						}))
					}
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		this.dateLabel =
			this.getTitleWidget('date_range', hidden, slide)?.content.text ??
			slide.dateRange?.label ??
			'';

		this.summaryText =
			this.getSummaryWidget(hidden, slide)?.content.text ??
			slide.description ??
			'';
		this.title =
			this.getTitleWidget('title', hidden, slide)?.content.text ??
			slide.title;
		this.titleColor =
			this.getTitleWidget('title', hidden, slide)?.content.color ??
			'#2872e1';

		const table = slide.widgets.find(
			w => w.kind === 'table' && !hidden.has(w.id)
		) as TableWidget | undefined;

		const rows = (table?.content.rows ?? []).map(r => ({
			hashtag: r['hashtag'] ?? '',
			count: parseInt(r['mentions'] ?? '0', 10)
		}));

		this.buildBars(rows);
	}

	private buildBars(rows: { hashtag: string; count: number }[]): void {
		const maxVal = rows.reduce((m, r) => Math.max(m, r.count), 0);

		this.bars = rows.slice(0, 10).map((r, i) => ({
			hashtag: r.hashtag,
			count: r.count,
			color: BAR_COLORS[i % BAR_COLORS.length],
			// TRAP: 85, not 100 — the value label follows the longest bar and needs
			// room inside the slide (top-sites-by-visits-slide does the same).
			widthPct: maxVal > 0 ? (r.count / maxVal) * 85 : 0
		}));

		// The same ten hashtags, each in the colour its bar has here.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels: this.bars.map(bar => bar.hashtag),
				series: [
					{
						key: 'mentions',
						label: 'Mentions',
						values: this.bars.map(bar => bar.count)
					}
				],
				labelColors: this.bars.map(bar => bar.color),
				labelColumn: 'Hashtag'
			})
		);

		// KPI callout — the single most-used hashtag.
		const top = rows.reduce(
			(best, r) => (r.count > best.count ? r : best),
			{ hashtag: '', count: 0 }
		);
		if (top.count > 0) {
			this.topHashtag = top.hashtag;
			this.topHashtagLabel = 'Top hashtag';
		} else {
			this.topHashtag = '';
			this.topHashtagLabel = '';
		}
	}

	private getTitleWidget(
		kind: TitleWidget['kind'],
		hidden: Set<string>,
		slide: PreviewSlide
	): TitleWidget | undefined {
		return slide.widgets.find(w => w.kind === kind && !hidden.has(w.id)) as
			| TitleWidget
			| undefined;
	}

	private getSummaryWidget(
		hidden: Set<string>,
		slide: PreviewSlide
	): SummaryTextWidget | undefined {
		return slide.widgets.find(
			w => w.kind === 'summary_text' && !hidden.has(w.id)
		) as SummaryTextWidget | undefined;
	}
}
